import type { ReactNode } from 'react';
import { SEV_DOT } from '../ui/Badges';
import { Notice } from '../ui/Feedback';
import { formatDateTime, formatDuration, formatNumber, SEVERITY_LABEL, SKIP_REASON_LABEL } from '../../lib/format';
import type { Review } from '../../types/api';
import { SEVERITIES } from '../../types/api';

const SEV_TEXT = {
  critical: 'text-sev-critical',
  high: 'text-sev-high',
  medium: 'text-sev-medium',
  low: 'text-sev-low',
  info: 'text-sev-info',
} as const;

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}

export function OverviewTab({ review }: { review: Review }) {
  const analyzed = review.files.filter((f) => f.analyzed);
  const skipped = review.files.filter((f) => !f.analyzed);

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <section className="card p-5 lg:col-span-2" aria-labelledby="ov-findings">
        <h2 id="ov-findings" className="text-sm font-semibold">
          Findings summary
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {SEVERITIES.map((s) => (
            <div key={s} className="relative overflow-hidden rounded-lg border border-line/70 bg-canvas/40 p-3">
              <span className={`absolute inset-x-0 top-0 h-1 ${SEV_DOT[s]}`} aria-hidden />
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <span className={`h-2 w-2 rounded-full ${SEV_DOT[s]}`} aria-hidden />
                {SEVERITY_LABEL[s]}
              </div>
              <p className={`mt-1 text-2xl font-semibold tabular-nums ${SEV_TEXT[s]}`}>{review.findingCounts[s]}</p>
            </div>
          ))}
        </div>
        {review.discardedFindingCount > 0 && (
          <p className="mt-3 text-xs text-muted">
            {review.discardedFindingCount} model finding(s) were discarded during validation because they were malformed, duplicated, or
            referenced files that were not part of the analyzed diff.
          </p>
        )}

        <h3 className="mt-6 text-sm font-semibold">Analysis coverage</h3>
        <p className="mt-1 text-sm text-muted">
          {analyzed.length} of {review.files.length} changed file(s) were sent for analysis.
        </p>
        {review.fileListTruncated && (
          <Notice tone="warn" className="mt-3">
            The PR has more files than the configured limit; the remaining files were not fetched.
          </Notice>
        )}
        {skipped.length > 0 && (
          <ul className="mt-3 divide-y divide-line rounded-md border border-line text-xs">
            {skipped.map((f) => (
              <li key={f.path} className="flex flex-wrap justify-between gap-2 px-3 py-2">
                <span className="break-all font-mono">{f.path}</span>
                <span className="text-muted">{f.skipReason ? SKIP_REASON_LABEL[f.skipReason] : 'Skipped'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card p-5" aria-labelledby="ov-details">
        <h2 id="ov-details" className="text-sm font-semibold">
          Review details
        </h2>
        <dl className="mt-4 grid grid-cols-2 gap-4">
          <Stat label="Depth" value={<span className="capitalize">{review.settings.depth}</span>} />
          <Stat label="Trigger" value={<span className="capitalize">{review.trigger}</span>} />
          <Stat label="Started" value={formatDateTime(review.startedAt ?? review.createdAt)} />
          <Stat label="Completed" value={formatDateTime(review.completedAt)} />
          <Stat label="Latency" value={formatDuration(review.usage?.latencyMs)} />
          <Stat label="Attempts" value={formatNumber(review.usage?.attempts)} />
          <Stat label="Input tokens" value={formatNumber(review.usage?.inputTokens)} />
          <Stat label="Output tokens" value={formatNumber(review.usage?.outputTokens)} />
          {review.usage?.estimatedCostUsd !== undefined && (
            <Stat label="Est. cost" value={`$${review.usage.estimatedCostUsd.toFixed(4)}`} />
          )}
          <Stat label="Published" value={`${review.publishedCommentCount} comment(s)`} />
        </dl>
        {review.settings.focus && (
          <div className="mt-4">
            <p className="label">Requested focus</p>
            <p className="mt-1 text-sm text-muted">{review.settings.focus}</p>
          </div>
        )}
        {review.expiresAt && <p className="mt-4 text-xs text-faint">Scheduled for deletion on {formatDateTime(review.expiresAt)} (data retention).</p>}
      </section>
    </div>
  );
}
